import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AlertService } from '@nano/common/alert';
import { MessageDirection, MessageService, MessageThread } from '@nano/common/message';
import { ApiEndpoints, ApiErrorCode, ApiService, httpErrorToApiError } from '@nano/core/api';
import { AuthService } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { ReplyToMessageThreadRequest } from './message-thread-details';

@Injectable({ providedIn: 'root' })
export class MessageThreadDetailsService {
  private userFullName: string;
  private bankName = inject(CONFIG).bankName;

  constructor(
    private domSanitizer: DomSanitizer,
    private apiService: ApiService,
    private alertService: AlertService,
    private authService: AuthService,
    private messageService: MessageService,
    private errorService: ErrorService
  ) {
    this.getUserFullName();
  }

  replyToMessageThread(id: string, request: ReplyToMessageThreadRequest): Observable<void> {
    return this.apiService.put<void>(ApiEndpoints.messageThread(id), request).pipe(
      take(1),
      tap(() => this.alertService.alert({ type: 'success', messageType: 'key', message: 'messages.sent', autoHide: true })),
      catchError((error: unknown) => {
        const apiError = httpErrorToApiError(error as HttpErrorResponse);
        if (apiError.code === (ApiErrorCode.communicationThrottlingException as string)) {
          this.errorService.showErrorModal(apiError);
          return EMPTY;
        }
        return throwError(() => error);
      })
    );
  }

  removeMessageThread(id: string): Observable<void> {
    return this.apiService.delete<void>(ApiEndpoints.messageThread(id)).pipe(
      take(1),
      tap(() => this.messageService.loadAllMessageThreads())
    );
  }

  markMessageThreadAsRead(id: string, body = { isRead: true }): Observable<void> {
    return this.apiService.patch<void>(ApiEndpoints.messageThread(id), body).pipe(
      take(1),
      tap(() => this.messageService.loadAllMessageThreads())
    );
  }

  getMessageThreadDetails(id: string): Observable<MessageThread> {
    return this.apiService.get<MessageThread>(ApiEndpoints.messageThread(id)).pipe(
      take(1),
      map((thread: MessageThread) => ({
        ...thread,
        messages: thread.messages.map((message) => ({
          ...message,
          body: this.domSanitizer.bypassSecurityTrustHtml(String(message.body ?? '').replace(/\n/g, '<br>')) as string,
          avatarName:
            message.direction === MessageDirection.OUT ? message.senderFullName?.toUpperCase() || this.userFullName?.toUpperCase() : this.bankName.toUpperCase()
        }))
      }))
    );
  }

  private getUserFullName(): void {
    this.authService.userProfile$
      .pipe(
        take(1),
        filter((userProfile) => !!userProfile)
      )
      .subscribe((userProfile) => (this.userFullName = userProfile.fullName));
  }
}
